/**
 * Formatting utilities for video annotation and documentation.
 * Uses shared formatters to ensure consistency between documentation and actual output.
 */

import { TextAlignment, VideoData } from '@lumen5/luminary';

import { ASSET_TYPES, TALKING_HEAD_MEDIA } from '../constants';
import type {
    ColorAssetDescription,
    DocumentationExample,
    FancyCaptionAssetDescription,
    GroupAssetDescription,
    IconAssetDescription,
    MediaAssetDescription,
    StickerAssetDescription,
    TextAssetDescription,
} from '../types';

import { formatAsset } from './assetFormatters';

import { TOOL_NAMES } from 'js/src/agent/toolNames';

/**
 * Shown only on assets that do not simply appear with their scene, so its absence reads as
 * "visible from the scene's first frame" rather than as missing information.
 */
const APPEARS_AT_DOC =
    '<when this asset appears, as the transcript word being spoken (e.g. "password") or milliseconds into the scene; absent means it is visible from the start of the scene>';

const colorAssetFormat = (): string => {
    const example: ColorAssetDescription = {
        type: ASSET_TYPES.BACKGROUND,
        name: '<asset_name>',
        bounds: '<x y width height>',
        color: '<color as hex, e.g. #ff0000>',
    };
    return '\n' + formatAsset(example);
};

const textAssetFormat = (): string => {
    const example: DocumentationExample<TextAssetDescription> = {
        type: ASSET_TYPES.TEXT,
        name: '<asset_name>',
        bounds: '<x y width height>',
        text: '<text content; **highlight** and <b>bold</b>/<i>italic</i>/<u>underline</u> markers are formatting, not content>',
        alignment: `<${TextAlignment.LEFT} | ${TextAlignment.CENTER} | ${TextAlignment.RIGHT}>`,
        // A role-labelled legend of every colour the text carries. Each entry names what the colour
        // IS (role), its current hex (+ template slot), and — for boxes — its alpha and fillType.
        colors: [
            { handle: 'text', role: 'text', value: '#0b1425 [slot 3]' },
            { handle: 'highlight', role: 'highlight', value: '#e8a65d [slot 1]' },
            { handle: 'box_1', role: 'backdrop', value: '#12343b', alpha: 0.6, fillType: 'solid' },
            { handle: 'shadow', role: 'shadow', value: '#000000', alpha: 0.5 },
        ],
        lines: '<number of text lines>',
        font: '<font family>',
        fontSize: '<font size>',
        animated:
            '<true|false — whether the text plays its animation; omitted if the design does not animate it>',
        appearsAt: APPEARS_AT_DOC,
    };
    return '\n' + formatAsset(example as TextAssetDescription);
};

const fancyCaptionAssetFormat = (): string => {
    const example: DocumentationExample<FancyCaptionAssetDescription> = {
        type: ASSET_TYPES.FANCY_CAPTION,
        name: '<asset_name>',
        bounds: '<x y width height>',
        alignment: `<${TextAlignment.LEFT} | ${TextAlignment.CENTER} | ${TextAlignment.RIGHT}>`,
        // The wording is a slice of the transcript and can't be edited as text, but the colours can:
        // the glyph fill (`text`), the spoken-word colour (`spoken_word`, when the design enables it),
        // and any box behind it — each set with set_text_color by handle.
        colors: [
            { handle: 'text', role: 'text', value: '#ffffff [slot 0]' },
            { handle: 'spoken_word', role: 'spoken_word', value: '#e8a65d [slot 1]' },
        ],
        font: '<font family>',
        fontSize: '<font size>',
        appearsAt: APPEARS_AT_DOC,
    };
    return '\n' + formatAsset(example as FancyCaptionAssetDescription);
};

const iconAssetFormat = (): string => {
    const example: IconAssetDescription = {
        type: ASSET_TYPES.ICON,
        name: '<asset_name>',
        bounds: '<x y width height>',
        mediaId: '<icon ID>',
        iconColors: '<comma-separated list of colors as hex, e.g. #ff0000>',
    };
    return '\n' + formatAsset(example);
};

const groupAssetFormat = (): string => {
    const example: GroupAssetDescription = {
        type: ASSET_TYPES.GROUP,
        name: '<asset_name>',
        bounds: '<x y width height>',
        children: '<comma-separated list of child asset names>',
        childrenAlignment: `<${VideoData.LEFT_ALIGNMENT} | ${VideoData.CENTER_ALIGNMENT} | ${VideoData.RIGHT_ALIGNMENT}>`,
        // Only present when the group draws a backdrop/underline/accent bar behind its children —
        // the group's own colour, addressed here rather than on the child text it sits behind.
        colors: [
            { handle: 'box_1', role: 'backdrop', value: '#0b1f24', alpha: 0.7, fillType: 'solid' },
        ],
        appearsAt: APPEARS_AT_DOC,
    };
    return '\n' + formatAsset(example);
};

const mediaAssetFormat = (): string => {
    const example: DocumentationExample<MediaAssetDescription> = {
        type: ASSET_TYPES.MEDIA,
        name: '<asset_name>',
        bounds: '<x y width height>',
        mediaId: '<media ID>',
        description: '<description>',
        mediaType: '<image/video/unknown>',
        position:
            '<x y> — where the media box sits on the canvas; distinct from mediaTranslation, which pans the media within its own frame',
        mediaScale: '<scale%>',
        mediaResolution: '<width height>',
        mediaTranslation: '<x(left|right|center) y(up|down|center)>',
        mediaXTranslationRange: '<[min, max]>',
        mediaYTranslationRange: '<[min, max]>',
        brightness: '<current brightness multiplier>',
    };
    return '\n' + formatAsset(example as MediaAssetDescription);
};

const stickerAssetFormat = (): string => {
    const example: StickerAssetDescription = {
        type: ASSET_TYPES.STICKER,
        name: '<asset_name>',
        bounds: '<x y width height>',
        mediaId: '<media ID>',
    };
    return '\n' + formatAsset(example);
};

export const getAssetAnnotationFormat = (): string => {
    return (
        groupAssetFormat() +
        textAssetFormat() +
        fancyCaptionAssetFormat() +
        iconAssetFormat() +
        mediaAssetFormat() +
        stickerAssetFormat() +
        colorAssetFormat()
    );
};

export const getHowOurVideosWorkGuide = (sceneAddressing: 'scene_id' | 'scene_index'): string => {
    // Toolbox addresses scenes by their stable scene_id; legacy surfaces address by positional
    // scene_index and consume an index-based representation, so the addressing guidance differs.
    const addressingBullet =
        sceneAddressing === 'scene_id'
            ? '- Every scene has a stable `scene_id` and every scene group a stable `scene_group_id`, each shown in its header (`@@ Scene scene_id <id> @@` and `@@ Scene Group <id> @@`). Always target a scene or scene group in tool calls by that id — never by a positional number, which shifts as scenes and groups are added. When inserting or moving a scene group, position it relative to an existing group with `after_scene_group_id` (omit it to place the group at the very start). Users are shown 1-based numbers in the UI, so use 1-based numbers when communicating with users.'
            : '- Scene indexes are 0-based but users are shown 1-based indexes in the UI. When referring to scenes, always use 0-based indexing internally and 1-based indexing when communicating with users.';
    return `## How our videos work

There are certain specifics to how our videos work that you should be aware of:
- Assets on a scene cannot be added or removed, only modified. A scene design is a fixed set of assets in a fixed layout, and what you can do to an asset depends on its type: TEXT — write, clear, and restyle its content/font/size/layout with \`${TOOL_NAMES.setText}\`, set any of its colours (glyph fill, highlight fill, or an existing background box/panel behind it) with \`${TOOL_NAMES.setTextColor}\` by the handle in its colour map, and turn its entrance/exit animation on or off with \`${TOOL_NAMES.setTextAnimation}\` (its \`animated\` field shows the current state); ${ASSET_TYPES.FANCY_CAPTION} — an on-screen animated caption; its wording is transcript-driven and not editable, but its colours (glyph fill \`text\`, the spoken-word colour \`spoken_word\`, or an existing box behind it) can be set with \`${TOOL_NAMES.setTextColor}\` by handle; MEDIA — fill or swap the media and reframe it within its frame (scale, crop, Ken Burns, brightness), but the frame itself is fixed; ICON — swap the icon, and set when it appears; BACKGROUND/RectangleAsset/CircleAsset/GradientAsset — recolor. You can never add an asset a design doesn't have, remove one, move a media frame, or add a background box where the text has none.
- Text assets with no text set are not displayed in the video.
- Videos must have at least one scene.
- Scene groups can be one of several types: "regular" (no audio — the on-screen text and media must communicate the whole message), "ai_voiceover" (AI-generated voiceover audio), "recorded_voiceover" (a human-recorded voiceover), "ai_talking_head" (an AI avatar speaking), or "recorded_talking_head" (a real person speaking on camera). Check the scene group type to understand how each part of the video communicates its message. A video can combine different group types (e.g. a text title scene, then a voiceover section, then a text outro).
${addressingBullet}
- "highlighted" text is displayed with "**" around it, indicating that it is rendered in a special, brand-styled way. Bold, italic, and underlined spans are shown with \`<b>…</b>\`, \`<i>…</i>\`, and \`<u>…</u>\` tags. All of these are formatting markers, not part of the actual content — remove them when reading or displaying the plain text, and re-add them (in the \`text\` you pass to \`${TOOL_NAMES.setText}\`) to keep the formatting. Prefer highlight for emphasis; apply bold/italic/underline only when the design or the user calls for it, never reflexively.`;
};

export const getVideoAnnotationGuide = (): string => {
    return `## Video Components & Assets
${getVideoAnnotationFormat()}
`;
};

export const getVideoAnnotationFormat = (): string => {
    return `
- CAPTIONS: the video's on-screen subtitles for the transcript, shown once under \`@@ Captions @@\`
  - Captions are GLOBAL, not per-scene: one design, size, position, color and font apply to the whole video, and \`${TOOL_NAMES.setCaptions}\` writes them all at once
  - The caption wording comes from the transcript and cannot be edited as text
- SCENE GROUP: groups one or more scenes that share timing (e.g., voiceover, talking head)
  - Types: regular (no audio, text), ai_voiceover, recorded_voiceover, ai_talking_head (AI avatar), recorded_talking_head
  - Contains: duration and — for AI voiceover groups — the voice and voiceSpeed
  - Voice-driven groups also carry their audio mix: voiceVolume (0-100, the level of the recording / voiceover track) and musicVolume (0-100, or \`auto\` when the music simply ducks under the voice). Both are set with \`${TOOL_NAMES.setSceneGroupVolume}\`; a non-voice group shows musicVolume only when a per-group override is set
- SCENE: contains assets that define the layout and content
  - Scenes are listed under their scene group's header; each scene belongs to exactly one group
  - Serialized with: design (its scene design id), duration, subscenes (count), covers (voice-driven groups only: the transcript words this scene plays over, verbatim, copy from here when a tool wants words, and the scene's role over them: overlay, uncovered, or speaker on screen), cues (the same words, each prefixed \`[N.Ns]\`: the second it is spoken, measured from this scene's start, so a reveal can be positioned on the word it belongs to), captionsVisible (only when captions apply to this scene's group: whether the global caption track renders here), transition (only when a match cut was applied to this scene's gap into the next one, not for the animation a scene sequence or an animated zoom builds in), textPositionPresets, and its assets
- Asset types include:
  - ${ASSET_TYPES.TEXT}: editable on-screen text
  - ${ASSET_TYPES.FANCY_CAPTION}: on-screen animated text that displays the transcript (applies to a single scene). Its wording likewise comes from the transcript and cannot be edited as text, but its colours can be set with \`${TOOL_NAMES.setTextColor}\` (glyph fill, the spoken word, and any box behind it)
  - ${ASSET_TYPES.MEDIA}: editable image or video. \`mediaId: ${TALKING_HEAD_MEDIA}\` (with \`linkedToSceneGroup: true\`) marks the presenter slot of a talking-head scene: it mirrors the scene group's recording in sync with the speech and holds no media of its own, so it can be reframed (scale, position) but never given other media — to show something else over the speaker, add a scene with a b-roll design instead
  - ${ASSET_TYPES.ICON}: editable icon graphic
  - ${ASSET_TYPES.READONLY_TEXT}, ${ASSET_TYPES.READONLY_MEDIA}, ${ASSET_TYPES.READONLY_ICON}: fixed artwork baked into the scene design (e.g. a looping background video, a decorative label). Its wording, media or icon can't be changed, replaced or cleared — leave it as it is. To show different media or text, edit the scene's editable ${ASSET_TYPES.MEDIA}/${ASSET_TYPES.TEXT} assets or choose a different scene design. A ${ASSET_TYPES.READONLY_TEXT}'s colours can still be set with \`${TOOL_NAMES.setTextColor}\`
  - ${ASSET_TYPES.GROUP}: non-visible alignment container for child assets
  - ${ASSET_TYPES.WATERMARK}: the video's own brand watermark, serialized with its position and opacity (0-100). Not the preview watermark on stock media, which disappears at render time
  - ${ASSET_TYPES.BACKGROUND}: solid background color
  - RectangleAsset, CircleAsset, GradientAsset: graphic shape. Each color it carries is listed under the property name \`${TOOL_NAMES.setAssetColor}\` takes (e.g. \`color\`, \`lineColor\`, \`colorStops.0.color\`)
  - ${ASSET_TYPES.MOTION_GRAPHIC}: a bespoke animated graphic (dynamic scene) authored as an HTML composition; serialized with its addressable parts and editable texts, or status "empty (awaiting generation)" before it has content`;
};

export const getDiffFormatGuide = (): string => {
    return `## Understanding Video State Diffs

Tools that modify the video automatically return a git-style diff showing what changed. This helps you track the evolution of the video over time.

### Diff Format Symbols
- \`+\` prefix: Property or asset was **added**
- \`-\` prefix: Property or asset was **removed**
- No prefix (context lines): Shows which asset is being modified
- \`@@ Section Name @@\`: Header indicating which part of video changed

### When You See Diffs
- **Initial state**: All properties shown with \`+\` (establishing baseline)
- **After tool execution**: Only changed properties shown with \`-\` (old) and \`+\` (new)
- **Modified assets**: Use shorthand \`asset: name\` to identify which asset changed

**Important**: Every tool that modifies the video will return a diff in this format. Use these diffs to track what changed and build your understanding of the current video state.`;
};
